import { useEffect } from 'react';

/**
 * Wiring for the installable half of the client app: the per-instance manifest
 * and the service worker, both bound to one instance's path.
 *
 * Every coach's clients install *their* coach's app. What makes that safe is
 * that both halves are scoped to `/c/<slug>/` rather than to the origin:
 *
 *   * `<link rel="manifest">` points at that instance's own manifest, which is
 *     generated per instance by `functions/c/[slug]/manifest.webmanifest.ts` and
 *     declares the same `scope` and `start_url`;
 *   * the service worker is registered with an explicit `scope` option, so the
 *     browser only ever hands it navigations under that instance, and its cache
 *     is keyed by the same path (`public/sw.js`).
 *
 * Registering `/sw.js` without that option would give it the default scope of
 * `/`, which is the mistake this function exists to make impossible: one
 * root-scoped worker holding one cached shell that every instance would then be
 * served (design.md F8).
 *
 * The coach's `/admin` surface registers nothing. It is desktop-first, is not
 * installable, and an offline admin shell that cannot save is worse than a
 * failed page load.
 */
export function useInstanceApp(slug: string | null): void {
  useEffect(() => {
    // Null for an unknown slug, a suspended instance and somebody else's
    // instance alike — nothing is installed for a surface that renders as not
    // found, and no worker is registered for a slug that is not ours.
    if (!slug) return;

    const scope = `/c/${slug}/`;

    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = `${scope}manifest.webmanifest`;
    document.head.append(link);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { scope }).catch((error: unknown) => {
        // Never fatal. Offline support is a convenience; the app works without
        // it, and a browser that refuses the registration (private mode, an
        // insecure context, storage disabled) must still show a training plan.
        console.warn('Service worker registration failed', error);
      });
    }

    return () => {
      link.remove();
    };
  }, [slug]);
}
